import apiClient from "../../api/api";

/**
 * Retrieves all manual time requests for a specific user.
 *
 * @async
 * @function getUserManualTimeRequests
 * @param {number|string} userId - Unique identifier of the user.
 * @returns {Promise<Array<Object>>} List of manual time requests.
 *
 * @example
 * await getUserManualTimeRequests(1097);
 */
export const getUserManualTimeRequests = async(userId) => {
    const getUserResponse = await apiClient.get(import.meta.env.VITE_USER_MANUAL_TIME_REQUESTS+`/${userId}`)
    
    return getUserResponse.data;
}

/**
 * Retrieves all requests (manual time, overtime, etc.) for a specific user.
 *
 * @async
 * @function getAllUserRequests
 * @param {number|string} userId - Unique identifier of the user.
 * @returns {Promise<Array<Object>>} List of user requests.
 *
 * @example
 * await getAllUserRequests(1097);
 */
export const getAllUserRequests = async(userId, params) => {
    const getAllUserRequestsResponse = await apiClient.get(import.meta.env.VITE_GET_USER_REQUESTS+`/${userId}/all`, {params})
    return getAllUserRequestsResponse.data;
}

/**
 * Creates a new manual time request.
 *
 * @async
 * @function createManualTimeRequest
 * @param {Object} manualTimeRequest - Payload containing manual time request data.
 * @returns {Promise<Object>} Created manual time request data.
 *
 * @example
 * await createManualTimeRequest({ date: "2025-12-15", hours: 2... });
 */
export const createManualTimeRequest = async(manualTimeRequest) => {
    const createManualTimeResponse = await apiClient.post(import.meta.env.VITE_CREATE_MANUAL_TIME_REQUEST, manualTimeRequest, {successToast:"Manual time request created"})
    
    return createManualTimeResponse
}

/**
 * Creates a new overtime request.
 *
 * @async
 * @function createOvertimeRequest
 * @param {Object} manualTimeRequest - Payload containing overtime request data.
 * @returns {Promise<Object>} Created overtime request data.
 *
 * @example
 * await createOvertimeRequest({ date: "2025-12-15", hours: 1.5... });
 */
export const createOvertimeRequest = async(manualTimeRequest) => {
    const createOvertimeResponse = await apiClient.post(import.meta.env.VITE_CREATE_OVERTIME_REQUEST, manualTimeRequest, {successToast:"Overtime request created"})

    return createOvertimeResponse
}

/**
 * Updates the status of a manual time request for a specific user and date.
 *
 * @async
 * @function requestUpdateManualTimeRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {string} date - Date of the manual time request (format: YYYY-MM-DD).
 * @param {number|string} projectId - Project identifier related to the request.
 * @param {number|string} managerId - Manager identifier responsible for the approval/rejection.
 * @param {Object} request - Payload containing the new status and related data.
 * @returns {Promise<Object>} API response after updating the manual time request.
 *
 * @example
 * await requestUpdateManualTimeRequest(
 *   1097,
 *   16,
 *   { status: "approved", comment: "Approved by manager" }
 * );
 */
export const requestUpdateManualTimeRequest = async(userId, requestId, request) => {
    const updateResponse = await apiClient.patch(import.meta.env.VITE_UPDATE_MANUAL_TIME_REQUEST+`/${userId}/${requestId}`, request, {successToast:"Manual time request updated"})
    return updateResponse
}

/**
 * Updates the status of an overtime request for a specific user and date.
 *
 * @async
 * @function updateOvertimeRequestRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {string} date - Date of the overtime request (format: YYYY-MM-DD).
 * @param {number|string} projectId - Project identifier related to the request.
 * @param {number|string} managerId - Manager identifier responsible for the approval/rejection.
 * @param {Object} request - Payload containing the new status and related data.
 * @returns {Promise<Object>} API response after updating the overtime request.
 *
 * @example
 * await updateOvertimeRequestRequest(
 *   1097,
 *   3,
 *   { status: "rejected", reasonId: 2 }
 * );
 */
export const updateOvertimeRequestRequest = async(userId, requestId, request) => {
    const updateResponse = await apiClient.patch(import.meta.env.VITE_UPDATE_OVERTIME_REQUEST+`/${userId}/${requestId}`, request, {successToast:"Overtime request updated"})
    
    return updateResponse
}

/**
 * Deletes a rejected manual time request for a specific user and date.
 *
 * @async
 * @function deleteRejectedManualTimeRequestRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {string} date - Date of the rejected request (format: YYYY-MM-DD).
 * @param {number|string} projectId - Project identifier related to the request.
 * @param {Object} request - Payload required by the API to perform deletion.
 * @returns {Promise<Object>} API response confirming deletion.
 *
 * @example
 * await deleteRejectedManualTimeRequestRequest(1097, "2025-12-15", 3, payload);
 */
export const deleteRejectedManualTimeRequestRequest = async(userId, date, projectId, request) => {
    const deleteResponse = await apiClient.delete(import.meta.env.VITE_DELETE_REJECTED_MANUAL_TIME_REQUEST+`/${userId}/dates/${date}?projectId=${projectId}`, request, {successToast:"Manual time deleted"})

    return deleteResponse
}

/**
 * Cancels a pending manual time request for a specific user and date.
 *
 * @async
 * @function cancelManualTimeRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {number} requestId - Unique identifier of the manual time request.
 * @returns {Promise<Object>} API response confirming the cancellation.
 *
 * @example
 * await cancelManualTimeRequest(1097, 16);
 */
export const cancelManualTimeRequest = async(userId, requestId) => {
    const cancelManualTimeResponse = await apiClient.delete(import.meta.env.VITE_DELETE_REJECTED_MANUAL_TIME_REQUEST+`/${userId}/${requestId}/pending`, {successToast:"Manual time cancelled"})

    return cancelManualTimeResponse.data
}

/**
 * Deletes a rejected overtime request for a specific user and date.
 *
 * @async
 * @function deleteRejectedOvertimeRequestRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {string} date - Date of the rejected request (format: YYYY-MM-DD).
 * @param {number} projectId - Unique identifier of the project.
 * @returns {Promise<Object>} API response confirming deletion.
 *
 * @example
 * await deleteRejectedOvertimeRequestRequest(1097, "2025-12-15", 16);
 */
export const deleteRejectedOvertimeRequestRequest = async(userId, date, projectId) => {
    const deleteResponse = await apiClient.delete(import.meta.env.VITE_DELETE_REJECTED_OVERTIME_REQUEST+`/${userId}/dates/${date}?projectId=${projectId}`, {successToast:"Overtime deleted"})

    return deleteResponse
}

/**
 * Cancels a pending overtime request for a specific user and date.
 *
 * @async
 * @function cancelOvertimeRequest
 * @param {number|string} userId - Unique identifier of the user.
 * @param {number} requestId - Unique identifier of the overtime request.
 * @returns {Promise<Object>} API response confirming the cancellation.
 *
 * @example
 * await cancelOvertimeRequest(1097, 16);
 */
export const cancelOvertimeRequest = async(userId, requestId) => {
    const deleteResponse = await apiClient.delete(import.meta.env.VITE_DELETE_REJECTED_OVERTIME_REQUEST+`/${userId}/${requestId}/pending`, {successToast:"Overtime cancelled"})

    return deleteResponse
}

/**
 * Retrieves the list of reject reasons based on request type.
 *
 * @async
 * @function getRejectReasonsRequest
 * @param {string} type - Request type used to filter reject reasons (e.g. "manual", "overtime").
 * @returns {Promise<Array<Object>>} List of reject reasons.
 *
 * @example
 * await getRejectReasonsRequest("manual");
 */
export const getRejectReasonsRequest = async(type) => {
    const getRejectReasonsResponse = await apiClient.get(import.meta.env.VITE_GET_REJECT_REASONS+`?type=${type}`)
    
    return getRejectReasonsResponse.data
}

